import { useCallback, useEffect, useRef, useState } from "react";
import * as apiClient from "@/services/api-client";
import type { AuditListItemExtended } from "@/types";
import { useDebouncedValue } from "./useDebouncedValue";

/** Matches list-params.ts's own floor -- a shorter query never hits the network. */
export const MIN_COMMAND_SEARCH_LENGTH = 2;
const DEBOUNCE_MS = 200;
const RESULT_LIMIT = 8;

/**
 * Debounced audit lookup backing the ⌘K command palette's "Audits" result
 * group (pirx-search). Mirrors useAuditHistory's fetchAudits requestId guard:
 * a stale response is dropped instead of clobbering a newer one.
 */
export function useCommandAuditSearch() {
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebouncedValue(query, DEBOUNCE_MS);
  const [results, setResults] = useState<AuditListItemExtended[]>([]);
  const [loading, setLoading] = useState(false);
  const requestSeqRef = useRef(0);

  useEffect(() => {
    const trimmed = debouncedQuery.trim();
    if (trimmed.length < MIN_COMMAND_SEARCH_LENGTH) {
      requestSeqRef.current += 1;
      setResults([]);
      setLoading(false);
      return;
    }

    const requestId = ++requestSeqRef.current;
    setLoading(true);
    void apiClient
      .listAuditHistory({
        q: trimmed,
        limit: RESULT_LIMIT,
        sortBy: "createdAt",
        sortOrder: "desc",
      })
      .then((data) => {
        if (requestId !== requestSeqRef.current) return;
        setResults(data.audits);
      })
      .catch(() => {
        if (requestId !== requestSeqRef.current) return;
        setResults([]);
      })
      .finally(() => {
        if (requestId !== requestSeqRef.current) return;
        setLoading(false);
      });
  }, [debouncedQuery]);

  const reset = useCallback(() => {
    requestSeqRef.current += 1;
    setQuery("");
    setResults([]);
    setLoading(false);
  }, []);

  return { query, setQuery, results, loading, reset };
}
